import React, { useEffect, useState, useCallback, useRef } from 'react';
import SelfApprovedChip from '../common/SelfApprovedChip';
import { useParams, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import {
    Box,
    Button,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    CircularProgress,
} from '@mui/material';
import { StocktakeService } from '../../services/stocktake.service';
import {
    StocktakeDTO,
    StocktakeCountDTO,
    STOCKTAKE_STATUS_LABELS,
} from '../../interfaces/stocktake.interface';
import { SegmentService } from '../../services/segment.service';
import { Segment } from '../../interfaces/accounting.interface';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import SearchableSelect from '../common/SearchableSelect';
import ConfirmDialog from '../common/ConfirmDialog';
import SerialNumbersField from '../serial/SerialNumbersField'; // F6b
import { useSerialTrackedItems } from '../serial/useSerialTrackedItems'; // F6b
import ItemScanField, { ScanOutcome } from '../item/ItemScanField';   // Q3
import { Item } from '../../interfaces/item.interface';

interface CountInput {
    countedQty: string;
    unitCost: string;
    segmentId: string;
}

const StocktakeDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const toast = useToast();
    const [stocktake, setStocktake] = useState<StocktakeDTO | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [actionInProgress, setActionInProgress] = useState<boolean>(false);
    const [counts, setCounts] = useState<Record<string, CountInput>>({});
    const [segments, setSegments] = useState<Segment[]>([]);
    const [confirming, setConfirming] = useState<'approve' | 'cancel' | null>(null);
    // ---- F6b: serial-tracked lines list every serial counted on the shelf ----
    const [countSerials, setCountSerials] = useState<Record<string, string[]>>({});
    const serialTracking = useSerialTrackedItems((stocktake?.lines ?? []).map((l) => l.itemId));

    const canManage = usePermission(PERMISSIONS.STOCK_MANAGE);

    const initCounts = (data: StocktakeDTO) => {
        const initial: Record<string, CountInput> = {};
        (data.lines ?? []).forEach((line) => {
            initial[line.id] = {
                countedQty: line.countedQty != null ? String(line.countedQty) : '',
                unitCost: line.unitCost != null ? String(line.unitCost) : '',
                segmentId: line.segmentId ?? '',
            };
        });
        setCounts(initial);
        setCountSerials({});   // F6b
    };

    const fetchStocktake = useCallback(async () => {
        if (!id) return;
        try {
            setLoading(true);
            const data = await StocktakeService.getStocktakeById(id);
            setStocktake(data);
            initCounts(data);
        } catch (err) {
            console.error('Error fetching stocktake:', err);
            toast.showApiError(err, 'Failed to load stocktake');
        } finally {
            setLoading(false);
        }
    }, [id, toast]);

    useEffect(() => {
        fetchStocktake();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        SegmentService.getByInstitution(0, 200)
            .then((page: { content: Segment[] }) => {
                setSegments((page.content ?? []).filter((s) => s.active !== false));
            })
            .catch((err: unknown) => {
                console.error('Failed to load segments', err);
            });
    }, []);

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '-';
        return format(parseISO(dateString), 'dd-MM-yyyy');
    };

    // ---- Q3: scan-to-count. The first scan of a line sets its counted qty to 1, each further scan adds 1. ----
    const scannedLines = useRef<Set<string>>(new Set());
    const countsRef = useRef<Record<string, CountInput>>({});
    countsRef.current = counts;
    const handleScan = (item: Item): ScanOutcome => {
        const label = `${item.itemCode} - ${item.itemName}`;
        const line = (stocktake?.lines ?? []).find((l) => l.itemId === item.id);
        if (!line) return { ok: false, message: `${label} is not on this stocktake.` };
        const first = !scannedLines.current.has(line.id);
        const cur = Number(countsRef.current[line.id]?.countedQty) || 0;
        const qty = first ? 1 : cur + 1;
        scannedLines.current.add(line.id);
        setCounts((prev) => ({ ...prev, [line.id]: { ...prev[line.id], countedQty: String(qty) } }));
        const serialNote = serialTracking.isTracked(line.itemId) === true ? ' List its serial numbers on the line.' : '';
        return { ok: true, message: `${label}: counted quantity is now ${qty}.${serialNote}` };
    };

    const handleCountChange = (lineId: string, field: keyof CountInput, value: string) => {
        setCounts((prev) => ({
            ...prev,
            [lineId]: { ...prev[lineId], [field]: value },
        }));
    };

    const handleSubmitCounts = async () => {
        if (!id || !stocktake) return;
        const payload: StocktakeCountDTO[] = [];
        for (const line of stocktake.lines ?? []) {
            const input = counts[line.id];
            if (!input || input.countedQty.trim() === '') continue;
            const qty = Number(input.countedQty);
            if (Number.isNaN(qty)) {
                toast.showError(`Invalid counted quantity for ${line.itemName}`);
                return;
            }
            const entry: StocktakeCountDTO = { lineId: line.id, countedQty: qty };
            // ---- F6b ----
            const lineSerials = countSerials[line.id] ?? [];
            if (serialTracking.isTracked(line.itemId) === true && lineSerials.length !== qty) {
                toast.showError(`List one serial number per counted unit for ${line.itemName} (${lineSerials.length} of ${qty})`);
                return;
            }
            if (lineSerials.length > 0) {
                entry.serials = lineSerials;
            }
            if (input.unitCost.trim() !== '') {
                const cost = Number(input.unitCost);
                if (Number.isNaN(cost)) {
                    toast.showError(`Invalid unit cost for ${line.itemName}`);
                    return;
                }
                entry.unitCost = cost;
            }
            if (input.segmentId) {
                entry.segmentId = input.segmentId;
            }
            payload.push(entry);
        }
        if (payload.length === 0) {
            toast.showError('Enter at least one counted quantity');
            return;
        }
        try {
            setActionInProgress(true);
            const updated = await StocktakeService.submitCounts(id, payload);
            setStocktake(updated);
            initCounts(updated);
            toast.showSuccess('Counts submitted');
        } catch (err) {
            console.error('Error submitting counts:', err);
            toast.showApiError(err, 'Failed to submit counts');
        } finally {
            setActionInProgress(false);
        }
    };

    const handleApprove = async () => {
        if (!id) return;
        setConfirming(null);
        try {
            setActionInProgress(true);
            const updated = await StocktakeService.approveStocktake(id);
            setStocktake(updated);
            initCounts(updated);
            toast.showSuccess('Stocktake approved');
        } catch (err) {
            console.error('Error approving stocktake:', err);
            toast.showApiError(err, 'Failed to approve stocktake');
        } finally {
            setActionInProgress(false);
        }
    };

    const handleCancel = async () => {
        if (!id) return;
        setConfirming(null);
        try {
            setActionInProgress(true);
            const updated = await StocktakeService.cancelStocktake(id);
            setStocktake(updated);
            initCounts(updated);
            toast.showSuccess('Stocktake cancelled');
        } catch (err) {
            console.error('Error cancelling stocktake:', err);
            toast.showApiError(err, 'Failed to cancel stocktake');
        } finally {
            setActionInProgress(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!stocktake) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>Stocktake not found.</Typography>
                <Button data-help="store.count.backNotFound" sx={{ mt: 2 }} variant="outlined" onClick={() => navigate('/stocktakes')}>
                    Back to List
                </Button>
            </Box>
        );
    }

    const isDraft = stocktake.status === 'DRAFT';
    const isPendingApproval = stocktake.status === 'PENDING_APPROVAL';
    const editable = isDraft && canManage;

    return (
        <Box sx={{ p: 3 }} data-help-page="store.count.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h4">Stocktake {stocktake.referenceNo}</Typography>
                <Button data-help="store.count.back" variant="outlined" onClick={() => navigate('/stocktakes')}>
                    Back to List
                </Button>
            </Box>

            <Paper elevation={3} sx={{ p: 3, mb: 3 }} data-help-section="store.count.header">
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                    <Box sx={{ minWidth: 160 }}>
                        <Typography variant="body2" color="text.secondary">Store</Typography>
                        <Typography>{stocktake.storeName || '-'}</Typography>
                    </Box>
                    <Box sx={{ minWidth: 160 }}>
                        <Typography variant="body2" color="text.secondary">Status</Typography>
                        <Typography>
                            {STOCKTAKE_STATUS_LABELS[stocktake.status] ?? stocktake.status}
                            <SelfApprovedChip selfApproved={stocktake.selfApproved} rule={stocktake.selfApprovalRule} />
                        </Typography>
                    </Box>
                    <Box sx={{ minWidth: 160 }}>
                        <Typography variant="body2" color="text.secondary">Stocktake Date</Typography>
                        <Typography>{formatDate(stocktake.stocktakeDate)}</Typography>
                    </Box>
                    <Box sx={{ minWidth: 160 }}>
                        <Typography variant="body2" color="text.secondary">Posted Date</Typography>
                        <Typography>{formatDate(stocktake.postedDate)}</Typography>
                    </Box>
                </Box>
                {stocktake.notes && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="body2" color="text.secondary">Notes</Typography>
                        <Typography>{stocktake.notes}</Typography>
                    </Box>
                )}
            </Paper>

            {editable && (
                <Box data-help="store.count.scan">
                <ItemScanField data-testid="stocktake-scan" onItem={handleScan}
                    label="Scan or type item barcode to count" />
                </Box>
            )}
            <TableContainer component={Paper} sx={{ mb: 3 }} data-help-section="store.count.lines">
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell data-help="store.count.col.no">#</TableCell>
                            <TableCell data-help="store.count.col.code">Item Code</TableCell>
                            <TableCell data-help="store.count.col.name">Item Name</TableCell>
                            <TableCell data-help="store.count.col.system" align="right">System Qty</TableCell>
                            <TableCell data-help="store.count.col.counted" align="right">Counted Qty</TableCell>
                            <TableCell data-help="store.count.col.cost" align="right">Unit Cost</TableCell>
                            <TableCell data-help="store.count.col.variance" align="right">Variance</TableCell>
                            {editable && <TableCell data-help="store.count.col.segment">Segment</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(stocktake.lines ?? []).length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={editable ? 8 : 7} align="center">
                                    No lines
                                </TableCell>
                            </TableRow>
                        ) : (
                            (stocktake.lines ?? []).map((line, index) => (
                                <TableRow key={line.id}>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>{line.itemCode}</TableCell>
                                    <TableCell>{line.itemName}</TableCell>
                                    <TableCell align="right">{line.systemQty}</TableCell>
                                    <TableCell align="right">
                                        {editable ? (
                                            <>
                                            <TextField
                                                size="small"
                                                type="number"
                                                data-help="store.count.field.counted" value={counts[line.id]?.countedQty ?? ''}
                                                onChange={(e) =>
                                                    handleCountChange(line.id, 'countedQty', e.target.value)
                                                }
                                                sx={{ width: 110 }}
                                            />
                                            {serialTracking.isTracked(line.itemId) !== false && (   /* F6b */
                                                <Box sx={{ mt: 1, minWidth: 240, textAlign: 'left' }}>
                                                    <SerialNumbersField mode="pick" label="Serials counted"
                                                        expected={Number(counts[line.id]?.countedQty) || 0}
                                                        value={countSerials[line.id] ?? []}
                                                        onChange={(v) => setCountSerials((prev) => ({ ...prev, [line.id]: v }))}
                                                        itemId={line.itemId} storeId={stocktake.storeId}
                                                        optional={serialTracking.isTracked(line.itemId) === undefined}
                                                        data-testid={`stocktake-serials-${line.id}`} />
                                                </Box>
                                            )}
                                            </>
                                        ) : (
                                            line.countedQty ?? '-'
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {editable ? (
                                            <TextField
                                                size="small"
                                                type="number"
                                                data-help="store.count.field.unitCost" value={counts[line.id]?.unitCost ?? ''}
                                                onChange={(e) =>
                                                    handleCountChange(line.id, 'unitCost', e.target.value)
                                                }
                                                sx={{ width: 110 }}
                                            />
                                        ) : (
                                            line.unitCost ?? '-'
                                        )}
                                    </TableCell>
                                    <TableCell align="right">{line.variance}</TableCell>
                                    {editable && (
                                        <TableCell>
                                            <SearchableSelect<Segment>
                                                options={segments}
                                                value={counts[line.id]?.segmentId ?? ''}
                                                onChange={id => handleCountChange(line.id, 'segmentId', id)}
                                                getOptionLabel={seg => `${seg.code} — ${seg.name}`}
                                                data-help="store.count.field.segment" placeholder="(default)"
                                                fullWidth={false}
                                                sx={{ width: 180 }}
                                            />
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', gap: 2 }}>
                {isDraft && canManage && (
                    <Button
                        data-help="store.count.submit"
                        variant="contained"
                        onClick={handleSubmitCounts}
                        disabled={actionInProgress}
                    >
                        Submit Counts
                    </Button>
                )}
                {isPendingApproval && canManage && (
                    <Button
                        data-help="store.count.approve"
                        variant="contained"
                        color="success"
                        onClick={() => setConfirming('approve')}
                        disabled={actionInProgress}
                    >
                        Approve
                    </Button>
                )}
                {(isDraft || isPendingApproval) && canManage && (
                    <Button
                        data-help="store.count.cancel"
                        variant="outlined"
                        color="error"
                        onClick={() => setConfirming('cancel')}
                        disabled={actionInProgress}
                    >
                        Cancel Stocktake
                    </Button>
                )}
            </Box>

            <ConfirmDialog
                open={confirming !== null}
                title={confirming === 'approve' ? 'Approve stocktake?' : 'Cancel stocktake?'}
                message={
                    confirming === 'approve'
                        ? 'Approving posts the counted differences to stock and the accounts straight away.'
                        : 'This stocktake will be cancelled and no stock will change. This cannot be undone.'
                }
                confirmLabel={confirming === 'approve' ? 'Approve' : 'Cancel stocktake'}
                cancelLabel={confirming === 'cancel' ? 'Keep stocktake' : 'Cancel'}
                confirmColor={confirming === 'cancel' ? 'error' : 'primary'}
                submitting={actionInProgress}
                onCancel={() => setConfirming(null)}
                onConfirm={() => (confirming === 'approve' ? handleApprove() : handleCancel())}
            />
        </Box>
    );
};

export default StocktakeDetails;
